import { createClient, type SupabaseClient } from "@supabase/supabase-js";

import type { LiveConfig } from "./live-config";

// Guard-namespaced auth storage key so a guard's Guard Web session never collides with the
// supervisor console (secops.console.auth) or the pilot console on the same origin/device.
const AUTH_STORAGE_KEY = "secops.guard.auth";

/**
 * Build the browser supabase-js client from an ALREADY-VALIDATED LiveConfig.
 *
 * The only sanctioned construction path: callers must pass a config that parseLiveConfig accepted,
 * so a secret-shaped key never reaches here. Auth uses the "stay-logged-in" model (session
 * persisted + silently refreshed) — guards should not have to re-auth mid-shift. `storage` is
 * injectable for tests.
 *
 * `detectSessionInUrl` is ON because Guard Web is the landing surface for Supabase Auth invite and
 * recovery links when the native app is not installed (product lock 2026-08-07, 1.3/F1). Without
 * it the officer arrives holding a valid link and gets a plain sign-in form they have no password
 * for. supabase-js consumes and strips the callback fragment; the reason for the visit is
 * snapshotted first by auth/auth-callback.ts. This is still not OAuth: there is no social login.
 */
export function createSupabaseClient(
  config: LiveConfig,
  options?: { storage?: SupabaseAuthStorage },
): SupabaseClient {
  return createClient(config.supabaseUrl, config.supabasePublishableKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: AUTH_STORAGE_KEY,
      ...(options?.storage ? { storage: options.storage } : {}),
    },
  });
}

/** Minimal storage shape supabase-js auth persistence requires (sync or async). */
export interface SupabaseAuthStorage {
  getItem: (key: string) => string | null | Promise<string | null>;
  setItem: (key: string, value: string) => void | Promise<void>;
  removeItem: (key: string) => void | Promise<void>;
}

export { AUTH_STORAGE_KEY };
